/**
 * Phase 20 — auto-send per campaign group, under the facts strip: every
 * group at once first (19/10/2026, two groups or more), one row per master
 * config (every container of the group runs under it), then what
 * stays country-wide — Kill, and the technical halt with its limit. A plan
 * without a master config says what to do first.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Send } from 'lucide-react';
import type { DropGenSchedulerPlanStatus, DropGenServerSendModes } from '@trafficpulse/shared';

import type { DropGenPlanDetail } from '../../../../drop-generator.types';
import { AllGroupsRow } from '../all/AllGroupsRow';
import { countryLabel } from '../autosend-fact';
import type { AutoSendLayer } from '../settings/autosend-draft';
import { CountryLimitField } from './CountryLimitField';
import { groupRows } from './group-rows';
import { GroupSendRow } from './GroupSendRow';

export const AutoSendGroupsPanel: FC<{
  plan: DropGenPlanDetail;
  status: DropGenSchedulerPlanStatus | undefined;
  server: DropGenServerSendModes | undefined;
  onSettings: (layer: AutoSendLayer) => void;
}> = ({ plan, status, server, onSettings }) => {
  const rows = groupRows(plan, status);
  return (
    <section aria-label="Auto-send per campaign group" className="overflow-hidden rounded-lg border border-border/60 bg-card text-xs animate-in fade-in-0 slide-in-from-top-1">
      <header className="flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b border-border/60 px-3 py-2">
        <span className="inline-flex items-center gap-2 whitespace-nowrap font-semibold">
          <Send className="h-3.5 w-3.5 text-muted-foreground" />
          Auto-send per campaign group
        </span>
        <span className="text-muted-foreground">— each group’s master config: its switch, daily cap and stop-loss; every container of the group runs under them</span>
      </header>
      {rows.length === 0 ? (
        <p className="px-3 py-3 text-muted-foreground">No campaign group has a master config yet — create one from the campaign group view; auto-send lives on it.</p>
      ) : (
        <>
          <AllGroupsRow plan={plan} groups={rows} />
          <ul className="divide-y divide-border/60">
            {rows.map((g) => (
              <GroupSendRow key={g.masterId} plan={plan} group={g} server={server} onSettings={onSettings} />
            ))}
          </ul>
        </>
      )}
      <footer className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t border-border/60 bg-muted/30 px-3 py-2">
        <span className="text-muted-foreground">{countryLabel(plan.countryKey)}-wide: Kill and a send that needs a person stop every group.</span>
        <CountryLimitField plan={plan} />
      </footer>
    </section>
  );
};
